// frontend/src/components/DocumentTemplateForm/DocumentTemplateForm.js

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../apiClient';
import './DocumentTemplateForm.css';

function DocumentTemplateForm() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [templateName, setTemplateName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [sections, setSections] = useState([]);
  const [clinicId, setClinicId] = useState('');
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient.get('/api/clinics')
      .then(res => setClinics(res.data))
      .catch(err => console.error('Kunde inte hämta kliniker:', err));
  }, []);

  useEffect(() => {
    if (id && id !== 'ny') {
      setLoading(true);
      apiClient.get(`/api/documentmallar/${id}`)
        .then((res) => {
          const template = res.data;
          setTemplateName(template.template_name || '');
          setPrompt(template.prompt || '');
          setSections(template.sections || []);
          setClinicId(template.clinic_id?.toString() || '');
        })
        .catch((err) => {
          console.error('Kunde inte ladda dokumentmall:', err);
          alert('Kunde inte ladda dokumentmallen.');
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  const handleAddSection = () => {
    setSections([
      ...sections,
      { section_title: '', content: '', order: sections.length + 1 },
    ]);
  };

  const handleRemoveSection = (index) => {
    const updated = [...sections];
    updated.splice(index, 1);
    setSections(updated);
  };

  const handleSectionChange = (index, field, value) => {
    const updated = [...sections];
    updated[index][field] = value;
    setSections(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!templateName || sections.length === 0) {
      setError("Mallnamn och minst en sektion krävs.");
      return;
    }

    const payload = {
      template_name: templateName,
      prompt: prompt,
      clinic_id: clinicId,
      sections: sections.map((section, index) => ({
        section_title: section.section_title,
        content: section.content,
        order: index,
      })),
    };

    try {
      setSaving(true);
      if (id && id !== 'ny') {
        await apiClient.put(`/api/documentmallar/${id}`, payload);
      } else {
        await apiClient.post('/api/documentmallar', payload);
      }

      navigate('/documentmallar');
    } catch (err) {
      console.error("Fel vid sparning av dokumentmall:", err);
      setError("Kunde inte spara dokumentmall");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Laddar mall...</div>;
  }

  return (
    <div className="document-template-form-container">
      <h2>{id && id !== 'ny' ? 'Redigera dokumentmall' : 'Skapa ny dokumentmall'}</h2>
      {error && <p className="error">{error}</p>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Mallnamn</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>Klinik</label>
          <select
            value={clinicId}
            onChange={(e) => setClinicId(e.target.value)}
            required
          >
            <option value="">-- Välj klinik --</option>
            {clinics.map(clinic => (
              <option key={clinic.clinic_id} value={clinic.clinic_id}>
                {clinic.clinic_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Huvudprompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
          />
        </div>

        <div>
          <h3>Sektioner</h3>
          {sections.map((section, index) => (
            <div key={index} className="section-form-group">
              <label>Titel</label>
              <input
                type="text"
                value={section.section_title}
                onChange={(e) =>
                  handleSectionChange(index, 'section_title', e.target.value)
                }
              />
              <label>Text</label>
              <textarea
                value={section.content}
                onChange={(e) =>
                  handleSectionChange(index, 'content', e.target.value)
                }
                rows={3}
              />
              <button
                type="button"
                className="remove-section-button"
                onClick={() => handleRemoveSection(index)}
              >
                Ta bort sektion
              </button>
            </div>
          ))}
          <button
            type="button"
            className="add-section-button"
            onClick={handleAddSection}
          >
            + Lägg till sektion
          </button>
        </div>

        <div className="form-actions">
          <button type="submit" className="save-button" disabled={saving}>
            {saving ? 'Sparar...' : 'Spara'}
          </button>
          <button
            type="button"
            className="cancel-button"
            onClick={() => navigate('/documentmallar')}
          >
            Avbryt
          </button>
        </div>
      </form>
    </div>
  );
}

export default DocumentTemplateForm;
